import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, IndianRupee, Wallet, Smartphone, Landmark, Receipt } from "lucide-react";
import { toast } from "sonner";

import api, { fmtINR, formatApiErrorDetail } from "@/lib/api";

const MODES = [
  { id: "cash", label: "Cash", icon: Wallet },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "bank", label: "Bank", icon: Landmark },
];

const DEFAULT_CATS = [
  "Salary", "Rent", "Electricity", "Internet", "Petrol", "Transport",
  "Office Expense", "Tea", "Food", "Loading", "Unloading", "Repair",
  "Maintenance", "Marketing", "Miscellaneous",
];

/**
 * QuickExpenseModal — single-step, PIN-fast expense entry.
 * Uses same /api/expenses endpoint as full ERP.
 */
export default function QuickExpenseModal({ open, onClose, onCreated }) {
  const [categories, setCategories] = useState(DEFAULT_CATS);
  const [category, setCategory] = useState("Miscellaneous");
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  useEffect(() => {
    if (!open) return;
    let t = setTimeout(() => onClose?.(), 180000);
    const reset = () => { clearTimeout(t); t = setTimeout(() => onClose?.(), 180000); };
    window.addEventListener("pointerdown", reset);
    window.addEventListener("keydown", reset);
    return () => { clearTimeout(t); window.removeEventListener("pointerdown", reset); window.removeEventListener("keydown", reset); };
  }, [open, onClose]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/expense-categories");
        if (data?.length) setCategories(data);
      } catch {}
    })();
    // reset
    setCategory("Miscellaneous"); setVendor(""); setAmount(""); setPaymentMode("cash"); setRemarks("");
  }, [open]);

  const save = async () => {
    if (!category || !Number(amount)) return;
    setSaving(true);
    try {
      await api.post("/expenses", { category, vendor, amount: Number(amount), payment_mode: paymentMode, remarks });
      toast.success("Expense recorded", { description: `${category} · ${fmtINR(Number(amount) || 0)}` });
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e?.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:px-4"
        style={{ background: "rgba(3,3,5,0.7)", backdropFilter: "blur(10px)" }}
        onClick={onClose}
      >
        <motion.div
          data-testid="quick-expense-modal"
          initial={{ opacity: 0, scale: 0.96, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 24 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong w-full max-w-lg rounded-t-3xl sm:rounded-3xl relative overflow-hidden max-h-[96vh] flex flex-col"
        >
          <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(255,46,147,0.18)" }} />
          <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(112,0,255,0.16)" }} />

          <div className="px-5 sm:px-7 py-4 sm:py-5 flex items-center justify-between border-b border-white/5 relative">
            <div>
              <p className="mono text-[10px] uppercase tracking-[0.3em] text-white/45">Quick entry</p>
              <h2 className="display text-2xl mt-1">New expense</h2>
            </div>
            <button data-testid="quick-expense-close" onClick={onClose} className="w-9 h-9 rounded-full glass flex items-center justify-center hover:border-white/25">
              <X size={14} />
            </button>
          </div>

          <div className="px-5 sm:px-7 py-5 sm:py-6 space-y-5 relative flex-1 overflow-y-auto">
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Category</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="quick-expense-categories">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                      category === c ? "bg-white text-black border-white" : "bg-white/[0.03] text-white/70 border-white/10 hover:border-white/25"
                    }`}
                  >{c}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Amount *</label>
              <div className="mt-2 relative">
                <IndianRupee size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="quick-expense-amount"
                  onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                  type="number" min="0" step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                  className="input pl-10 mono text-xl"
                />
              </div>
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Vendor (optional)</label>
              <input
                data-testid="quick-expense-vendor"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="e.g. APDCL, IOCL Pump…"
                className="mt-2 input"
              />
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Payment mode</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {MODES.map((m) => {
                  const active = paymentMode === m.id;
                  const Ic = m.icon;
                  return (
                    <button
                      key={m.id}
                      data-testid={`quick-expense-mode-${m.id}`}
                      onClick={() => setPaymentMode(m.id)}
                      className={`glass rounded-xl p-3 flex items-center gap-2 justify-center transition-all text-[13px] ${active ? "border-cyan-400/60 shadow-[0_0_22px_rgba(0,240,255,0.2)]" : "hover:border-white/25"}`}
                    >
                      <Ic size={13} className={active ? "text-cyan-300" : "text-white/60"} />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Remarks (optional)</label>
              <textarea
                data-testid="quick-expense-remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Notes"
                className="mt-2 input min-h-[60px]"
              />
            </div>
          </div>

          <div className="px-7 py-4 border-t border-white/5 flex items-center justify-between gap-3 relative">
            <div className="mono text-[11px] text-white/55">
              {category} · {fmtINR(amount)}
            </div>
            <button
              data-testid="quick-expense-save"
              disabled={saving || !category || !Number(amount)}
              onClick={save}
              className="rounded-full px-6 py-2.5 text-[13px] font-semibold flex items-center gap-2 bg-gradient-to-r from-pink-400 to-purple-500 text-black hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save expense"} <Check size={14} />
            </button>
          </div>

          <style>{`
            .input { width: 100%; padding: 0.75rem 0.9rem; border-radius: 0.75rem; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; outline: none; font-size: 13px; }
            .input:focus { border-color: rgba(0,240,255,0.55); box-shadow: 0 0 0 3px rgba(0,240,255,0.16); }
          `}</style>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
